import AddIcon from "@mui/icons-material/Add";
import {
    Chip,
    CircularProgress,
    Dialog,
    Grid,
    Stack
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";
import DialogHeader from "./Header";
import Label from "../Label";
import TextInput from "../TextInputs";
import { handleOpenSnackBar } from "../../utils";
import { addEditAttributeValidation } from "../../utils/Validation";
import { createAttribute, getSingleAttribute, updateAttribute } from "../../services/attributeFn";
import { translateText } from "../../utils/translationService";
import TranslateIcon from "@mui/icons-material/Translate";
import { useSelector } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing("24px"),
    },
    "& .MuiPaper-root": {
        width: "600px",
        [theme.breakpoints.down("sm")]: {
            margin: 12,
        },
    },
}));

const initialInputValues = {
    label_ro: "",
    label_en: "",
    order: 0,
};

const AddEditAttributeDialog = forwardRef(({ onConfirm }, ref) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation(["catalogue", "common"]);
    const [isLoading, setIsLoading] = useState({
        save: false,
        detail: false,
        translateToEnglish: false,
    });
    const [dialogData, setDialogData] = useState(null);
    const [inputError, setInputError] = useState({});
    const [inputValues, setInputValues] = useState(initialInputValues);

    const userData = useSelector((state) => state.userData.user);
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

    useImperativeHandle(ref, () => ({
        openDialog(params) {
            setDialogData(params);
            setOpen(true);
        },

        closeDialog() {
            setOpen(false);
        },
    }));

    const fetchSingleAttribute = async () => {
        try {
            setIsLoading((prev) => ({ ...prev, detail: true }));
            const response = await getSingleAttribute(dialogData?.data?._id);
            if (response?.data?.status === "success") {
                const data = response?.data?.data;
                setInputValues({
                    label_ro: data?.label?.ro,
                    label_en: data?.label?.en,
                    order: data?.sortOrder,
                });
            } else {
                handleOpenSnackBar("error", response?.data?.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error?.message);
        } finally {
            setIsLoading((prev) => ({ ...prev, detail: false }));
        }
    };

    useEffect(() => {
        if (dialogData?.type === "edit" && dialogData?.data?._id) {
            fetchSingleAttribute();
        }
    }, [dialogData?.type, dialogData?.data?._id]);

    const handleClose = () => {
        setOpen(false);
        setInputValues(initialInputValues);
        setInputError({});
        setDialogData(null);
    };

    const handleChangeValues = (e) => {
        const { name, value, valueAsNumber, type } = e.target;

        const formattedValue = type === "number" ? valueAsNumber : value;

        setInputValues((prev) => ({ ...prev, [name]: formattedValue }));
        setInputError((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSave = () => {
        const valid = addEditAttributeValidation(inputValues, setInputError);
        if (valid) return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));

        const payload = {
            label: {
                en: inputValues.label_en,
                ro: inputValues.label_ro,
            },
            sortOrder: inputValues.order,
            // createdBy: userData?.adminId,
        };

        if (dialogData?.type === "add") {
            handleApiCall(createAttribute, payload, "");
        } else {
            handleApiCall(updateAttribute, payload, dialogData?.data?._id);
        }
    };


    const handleApiCall = async (apiFunction, payload, id) => {
        try {
            setIsLoading((prev) => ({ ...prev, save: true }));
            const response = await apiFunction(id, payload);
            if (response?.data?.status === "success") {
                handleOpenSnackBar("success", response?.data?.message);
                handleClose();
                dialogData?.getData(searchKeys.attributeName);
            } else {
                handleOpenSnackBar("error", response?.data?.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, save: false }));
        }
    };

    const handleTranslateToEnglish = async (key1, key2) => {
        setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
        try {
            const [englishText] = await Promise.all([
                translateText(inputValues[key1]),
            ]);
            setInputValues((prev) => ({
                ...prev,
                [key2]: englishText,
            }));
            setInputError((prev) => ({
                ...prev,
                [key2]: "",
            }));
        } catch (error) {
            handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
        }
    };



    const headingText = dialogData?.type === "add" ? t("addAttribute") : t("editAttribute");
    const secondaryHeadingText = dialogData?.type === "add" ? t("addAttributeInstruction") : t("editAttributeInstruction");




    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
        >
            <DialogHeader
                title={headingText}
                secondaryHeading={secondaryHeadingText}
                onClose={handleClose}
                icon={<AddIcon />}
            />
            <DialogBody>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Label
                            labelText={t("attributeNameInRomanian")}
                            required
                            htmlFor={"label_ro"}
                        />
                        <TextInput
                            id={"label_ro"}
                            type={"text"}
                            placeholder={t("enterAttributeNameInRomanian")}
                            name={"label_ro"}
                            value={inputValues.label_ro}
                            onChange={handleChangeValues}
                            inputError={Boolean(inputError.label_ro)}
                            helperText={t(inputError.label_ro)}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack
                            mb={"5px"}
                            direction={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                        >
                            <Label
                                required
                                labelText={t("attributeNameInEnglish")}
                                htmlFor={`attribute-name-en`}
                                labelProps={{ marginBottom: 0 }}
                            />
                            <Chip
                                label={t("translateToEnglish", { ns: "common" })}
                                size="small"
                                onClick={() =>
                                    handleTranslateToEnglish("label_ro", "label_en")
                                }
                                icon={
                                    isLoading.translateToEnglish ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <TranslateIcon />
                                    )
                                }
                                disabled={isLoading.translateToEnglish}
                            />
                        </Stack>
                        <TextInput
                            id={"label_en"}
                            type={"text"}
                            placeholder={t("enterAttributeNameInEnglish")}
                            name={"label_en"}
                            value={inputValues.label_en}
                            onChange={handleChangeValues}
                            inputError={Boolean(inputError.label_en)}
                            helperText={t(inputError.label_en)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Label labelText={t("attributeOrder")} required htmlFor={"label"} />
                        <TextInput
                            id={"order"}
                            type={"number"}
                            placeholder={t("enterAttributeOrder")}
                            name={"order"}
                            value={inputValues.order}
                            onChange={handleChangeValues}
                            inputError={Boolean(inputError.order)}
                            helperText={t(inputError.order)}
                            required
                        />
                    </Grid>
                </Grid>


            </DialogBody>

            <DialogActionButtons
                onCancel={handleClose}
                onConfirm={handleSave}
                confirmText={dialogData?.type === "add" ? t("add") : t("edit")}
                cancelText={t("cancel")}
                isConfirmBtnDisable={isLoading.save}
                showCancelButton={true}
            />
        </BootstrapDialog>
    );
});

export default AddEditAttributeDialog;
